import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronRightIcon, EditIcon } from '@persona-id/design-tokens/icons';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  DEFAULT_FIELD_SCHEMA,
  EditorableSchema,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import ExpandableFieldRow, {
  NEST_SPACING,
} from 'dashboard/components-lib/FieldSchemaEditor/ExpandableFieldRow';
import { Inserter } from 'dashboard/components-lib/FieldSchemaEditor/Inserter';
import { Column } from 'dashboard/components-lib/FieldSchemaEditor/table';
import { hasNestedSchemas } from 'dashboard/components-lib/FieldSchemaEditorForm/utils';
import Spacer from 'design-system/components/Spacer';
import { TableBody, TableCell, TableRow } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { isFocused } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';
import { FieldSchemaType } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';

const HighlightableTableRow = styled(TableRow)<{ hasEmphasis: boolean; hasError: boolean }>`
  cursor: pointer;
  background-color: ${({ hasEmphasis, theme }) =>
    hasEmphasis ? theme.color.background.highlight : 'transparent'};
  ${({ hasError, theme }) =>
    hasError ? `background-color: ${theme.color.variants.danger.highlight}` : ''};

  &${isFocused} {
    z-index: 1;
    position: relative;
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.color.variants.primary.highlight};
  }
`;

const StyledTableCell = styled(TableCell)<{ noBorder: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ noBorder }) => (noBorder ? 'border: 0' : '')}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledChevronIcon = styled(ChevronRightIcon)<{ expanded: number }>`
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0')});
  transition: transform ${TransitionDuration.Fast};
  margin-right: ${Spacing[1]};
`;

const TooltipContainer = styled.div`
  display: inline-flex;
  color: ${Color.CoreYellow800};
  margin-left: ${Spacing[1]};
`;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.color.text.tertiary};
  font-style: italic;
  margin-right: ${Spacing[1]};
`;

interface FieldRowProps {
  fieldSchema: EditorableSchema;
  fieldNamePrefix: string;
  fieldIndex: number;
  columns: Column[];
  selectedFieldSchemaPath: string;
  nestLevel?: number;
  editable: boolean;
  onInsertSibling: (index: number, schema: EditorableSchema) => void;
  selectFieldSchema: (fieldSchemaPath: string) => void;
}

const NestedFieldRow: React.FC<FieldRowProps> = ({
  fieldNamePrefix,
  fieldSchema,
  fieldIndex,
  columns = [],
  selectedFieldSchemaPath,
  nestLevel = 0,
  editable,
  onInsertSibling,
  selectFieldSchema,
}) => {
  const fieldName = `${fieldNamePrefix}.${fieldIndex}`;

  const { getFieldState } = useFormContext();
  const [expanded, { toggle: toggleExpansion, enable: enableExpansion }] = useToggle(true);
  const insertSiblingIndex = fieldIndex + 1;

  let arrayName;
  let nestedSchemas: EditorableSchema[];
  if (fieldSchema.type === FieldSchemaType.Hash) {
    arrayName = `${fieldName}.config.item-schemas`;
    nestedSchemas = fieldSchema.config['item-schemas'];
  } else {
    // We assume the schema is Array with item schema Hash
    arrayName = `${fieldName}.config.item-schema.config.item-schemas`;
    nestedSchemas = fieldSchema.config['item-schema'].config['item-schemas'];
  }

  // @ts-expect-error - useFieldArray fails to compute type because EditorableSchema is infinitely nestable
  const fieldArray = useFieldArray<{ [key: string]: EditorableSchema[] }>({
    name: arrayName,
  });

  // TODO: There's an opportunity to refactor `FieldRow` and `NestedFieldRow` to share
  //  common components in the interest of keeping them in sync
  return (
    <TableBody>
      <HighlightableTableRow
        tabIndex={0}
        id={`${fieldName}__table-row`}
        hasError={getFieldState(fieldName)?.error != null}
        hasEmphasis={selectedFieldSchemaPath === fieldName}
        aria-label={`Expand ${fieldSchema.label} field`}
        onClick={() => {
          toggleExpansion();
          selectFieldSchema(fieldName);
        }}
      >
        <StyledTableCell noBorder={expanded}>
          <FlexContainer>
            <Spacer width={4 * nestLevel} height={0} />
            <FlexContainer aria-label={expanded ? 'Hide nested fields' : 'Show nested fields'}>
              <StyledChevronIcon size={14} expanded={expanded ? 1 : 0} />
            </FlexContainer>
            <span>{fieldSchema?.key || <PlaceholderText>Unnamed Field</PlaceholderText>}</span>
            {!fieldSchema.persisted && (
              <TooltipContainer>
                <Tooltip side='top' iconTrigger={<EditIcon size={11} />}>
                  <Paragraph color='primary' size='sm'>
                    Unsaved changes
                  </Paragraph>
                </Tooltip>
              </TooltipContainer>
            )}
          </FlexContainer>
        </StyledTableCell>
        {columns.map((column, index) => (
          <StyledTableCell
            key={column.header + index}
            noBorder={expanded}
            style={{ width: column.width ?? 'auto' }}
          >
            <FlexContainer>
              {column.generateContent({
                field: fieldSchema,
                fieldName: fieldName,
                prefix: nestLevel === 0 ? fieldSchema.key : fieldName,
                nestLevel: nestLevel,
                disabled: !editable,
                highlighted: selectedFieldSchemaPath === fieldName,
                appendChild: () => {
                  fieldArray.append(DEFAULT_FIELD_SCHEMA);
                  selectFieldSchema(`${arrayName}.${fieldArray.fields.length}`);
                  enableExpansion();
                },
                insertSibling: (fieldSchema: EditorableSchema) => {
                  onInsertSibling(insertSiblingIndex, fieldSchema);
                  selectFieldSchema(`${fieldNamePrefix}.${insertSiblingIndex}`);
                },
                updateSelf: () => {},
                setFocusedSchema: () => {},
              })}
            </FlexContainer>
          </StyledTableCell>
        ))}
      </HighlightableTableRow>
      <ExpandableFieldRow
        colSpan={columns.length + 1}
        nestLevel={nestLevel}
        visible={expanded}
        showBottomBorder
      >
        {nestedSchemas.map((nestedSchema, index) => {
          const nestedFieldName = `${arrayName}.${index}`;
          return (
            <FormFieldRow
              key={nestedFieldName}
              fieldNamePrefix={arrayName}
              fieldIndex={index}
              fieldSchema={nestedSchema}
              columns={columns}
              selectedFieldSchemaPath={selectedFieldSchemaPath}
              nestLevel={nestLevel + 1}
              editable={editable}
              onInsertSibling={fieldArray.insert}
              selectFieldSchema={selectFieldSchema}
            />
          );
        })}
      </ExpandableFieldRow>
      {editable && !expanded && (
        <Inserter
          prefix=''
          colSpan={columns.length + 1}
          nestSpacing={NEST_SPACING * nestLevel}
          onAddRow={() => {
            onInsertSibling(insertSiblingIndex, DEFAULT_FIELD_SCHEMA);
            selectFieldSchema(`${fieldNamePrefix}.${insertSiblingIndex}`);
          }}
        />
      )}
    </TableBody>
  );
};

const FieldRow: React.FC<FieldRowProps> = ({
  fieldSchema,
  fieldNamePrefix,
  fieldIndex,
  columns = [],
  selectedFieldSchemaPath,
  nestLevel = 0,
  editable,
  onInsertSibling,
  selectFieldSchema,
}) => {
  const fieldName = `${fieldNamePrefix}.${fieldIndex}`;
  const { getFieldState } = useFormContext();
  const insertSiblingIndex = fieldIndex + 1;

  // TODO: There's an opportunity to refactor `FieldRow` and `NestedFieldRow` to share
  //  common components in the interest of keeping them in sync
  return (
    <TableBody>
      <HighlightableTableRow
        tabIndex={0}
        id={`${fieldName}__table-row`}
        hasError={getFieldState(fieldName)?.error != null}
        hasEmphasis={selectedFieldSchemaPath === fieldName}
        aria-label={`Expand ${fieldSchema.label} field`}
        onClick={() => selectFieldSchema(fieldName)}
      >
        <StyledTableCell noBorder={false}>
          <FlexContainer>
            <Spacer width={4 * nestLevel} height={0} />
            <span>{fieldSchema?.key || <PlaceholderText>Unnamed Field</PlaceholderText>}</span>
            {!fieldSchema.persisted && (
              <TooltipContainer>
                <Tooltip side='top' iconTrigger={<EditIcon size={11} />}>
                  <Paragraph color='primary' size='sm'>
                    Unsaved changes
                  </Paragraph>
                </Tooltip>
              </TooltipContainer>
            )}
          </FlexContainer>
        </StyledTableCell>
        {columns.map((column, index) => (
          <StyledTableCell
            key={column.header + index}
            noBorder={false}
            style={{ width: column.width ?? 'auto' }}
          >
            <FlexContainer>
              {column.generateContent({
                field: fieldSchema,
                fieldName: fieldName,
                prefix: nestLevel === 0 ? fieldSchema.key : fieldName,
                nestLevel: nestLevel,
                disabled: !editable,
                highlighted: selectedFieldSchemaPath === fieldName,
                appendChild: () => {},
                insertSibling: (fieldSchema: EditorableSchema) => {
                  onInsertSibling(insertSiblingIndex, fieldSchema);
                  selectFieldSchema(`${fieldNamePrefix}.${insertSiblingIndex}`);
                },
                updateSelf: () => {},
                setFocusedSchema: () => {},
              })}
            </FlexContainer>
          </StyledTableCell>
        ))}
      </HighlightableTableRow>
      {editable && (
        <Inserter
          prefix=''
          colSpan={columns.length + 1}
          nestSpacing={NEST_SPACING * nestLevel}
          onAddRow={() => {
            onInsertSibling(insertSiblingIndex, DEFAULT_FIELD_SCHEMA);
            selectFieldSchema(`${fieldNamePrefix}.${insertSiblingIndex}`);
          }}
        />
      )}
    </TableBody>
  );
};

interface FormFieldRowProps {
  fieldNamePrefix: string;
  fieldIndex: number;
  fieldSchema: EditorableSchema;
  columns: Column[];
  selectedFieldSchemaPath: string;
  nestLevel?: number;
  editable: boolean;
  onInsertSibling: (index: number, schema: EditorableSchema) => void;
  selectFieldSchema: (fieldSchemaPath: string) => void;
}

const FormFieldRow: React.FC<FormFieldRowProps> = (props) => {
  if (hasNestedSchemas(props.fieldSchema)) {
    return <NestedFieldRow {...props} />;
  }

  return <FieldRow {...props} />;
};

export default FormFieldRow;
